import { z } from 'zod';

/**
 * Actor provenance — "who really did this, and on whose behalf".
 *
 * Every auditable mutation carries a formal `actorUserId` (the row's actor),
 * but that alone can't distinguish a human action from one a человек asked the
 * AI to do, or one an autonomous agent performed.  This envelope closes that
 * gap so the activity feed / notifications / audit can later be analysed
 * honestly:
 *
 *   - 'user'        — a person did it directly.  Default for legacy + normal rows.
 *   - 'ai_assisted' — a person used an AI feature (parse-task, copilot, …);
 *                     `aiActionLogId` links to the concrete AI call.
 *   - 'agent'       — an autonomous agent did it; `agentId` + `agentRunId` +
 *                     `triggerType` say which agent and why it woke up.
 *   - 'system'      — the platform itself (migrations, schedulers).
 */
export const ACTOR_KINDS = ['user', 'agent', 'ai_assisted', 'system'] as const;
export const ActorKindSchema = z.enum(ACTOR_KINDS);
export type ActorKind = z.infer<typeof ActorKindSchema>;

/**
 * The extra provenance fields stored alongside `actorKind` (as JSON on audit
 * rows).  All optional — a plain 'user' action carries none of them.
 */
export const ActorProvenanceSchema = z.object({
  /** The user the action was performed on behalf of (PAT owner for agents). */
  onBehalfOf: z.string().uuid().nullable().optional(),
  /** Agent definition id, when actorKind === 'agent'. */
  agentId: z.string().uuid().nullable().optional(),
  /** The specific agent run that produced this action. */
  agentRunId: z.string().uuid().nullable().optional(),
  /** What woke the agent: 'mention' | 'event' | 'cron' | 'manual' | 'webhook'. */
  triggerType: z.string().nullable().optional(),
  /** Link to the concrete AI call in ai_action_log (ai_assisted / agent). */
  aiActionLogId: z.string().uuid().nullable().optional(),
});
export type ActorProvenance = z.infer<typeof ActorProvenanceSchema>;
